import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Trash, Warning, HardHat, PencilSimple, Export } from "@phosphor-icons/react";
import { api, apiError, downloadFile } from "@/lib/api";
import { useLang } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { DateField } from "@/components/DateField";

const INCIDENT_TYPES = ["near_miss", "first_aid", "medical_treatment", "lost_time", "fatality"];
const INCIDENT_STATUSES = ["open", "investigating", "closed"];
const SEV_STYLE = ["", "bg-emerald-100 text-emerald-700", "bg-lime-100 text-lime-700", "bg-amber-100 text-amber-700", "bg-orange-100 text-orange-700", "bg-red-100 text-red-700"];

const KPI = ({ label, value, color = "text-slate-900", testid }) => (
  <div className="kpi-card bg-white border border-slate-200 rounded-lg p-6" data-testid={testid}>
    <p className="overline-label">{label}</p>
    <p className={`font-heading font-black text-3xl tracking-tight mt-2 ${color}`}>{value}</p>
  </div>
);

export default function Safety() {
  const { t } = useLang();
  const { user } = useAuth();
  const [kpis, setKpis] = useState(null);
  const [incidents, setIncidents] = useState([]);
  const [inspections, setInspections] = useState([]);
  const [incOpen, setIncOpen] = useState(false);
  const [inspOpen, setInspOpen] = useState(false);
  const [incForm, setIncForm] = useState({ date: "", type: "near_miss", severity: 1, description: "", area: "", status: "open", days_lost: 0 });
  const [inspForm, setInspForm] = useState({ date: "", area: "", inspector: "", findings: "", status: "open", compliance_score: 100 });
  const [editingInc, setEditingInc] = useState(null);
  const [editingInsp, setEditingInsp] = useState(null);
  const [manHours, setManHours] = useState("");
  const canWrite = ["admin", "manager", "supervisor"].includes(user?.role);
  const canManage = ["admin", "manager"].includes(user?.role);

  const load = useCallback(() => {
    api.get("/safety/kpis").then((r) => setKpis(r.data));
    api.get("/safety/incidents").then((r) => setIncidents(r.data));
    api.get("/safety/inspections").then((r) => setInspections(r.data));
    api.get("/settings/safety").then((r) => setManHours(r.data.man_hours));
  }, []);
  useEffect(() => { load(); }, [load]);

  const addIncident = async () => {
    try {
      const payload = { ...incForm, severity: Number(incForm.severity), days_lost: Number(incForm.days_lost) };
      let res;
      if (editingInc) res = await api.put(`/safety/incidents/${editingInc}`, payload);
      else res = await api.post("/safety/incidents", payload);
      const offline = res?.data?._offline;
      toast.success(offline ? t("savedOffline") : "✓");
      setIncOpen(false);
      setIncForm({ date: "", type: "near_miss", severity: 1, description: "", area: "", status: "open", days_lost: 0 });
      if (offline) {
        setIncidents((prev) => {
          if (editingInc) return prev.map((x) => (x.id === editingInc ? { ...x, ...res.data, id: editingInc } : x));
          return [res.data, ...prev];
        });
      } else {
        load();
      }
    } catch (e) { toast.error(apiError(e)); }
  };

  const addInspection = async () => {
    try {
      const payload = { ...inspForm, compliance_score: Number(inspForm.compliance_score) };
      let res;
      if (editingInsp) res = await api.put(`/safety/inspections/${editingInsp}`, payload);
      else res = await api.post("/safety/inspections", payload);
      const offline = res?.data?._offline;
      toast.success(offline ? t("savedOffline") : "✓");
      setInspOpen(false);
      setInspForm({ date: "", area: "", inspector: "", findings: "", status: "open", compliance_score: 100 });
      if (offline) {
        setInspections((prev) => {
          if (editingInsp) return prev.map((x) => (x.id === editingInsp ? { ...x, ...res.data, id: editingInsp } : x));
          return [res.data, ...prev];
        });
      } else {
        load();
      }
    } catch (e) { toast.error(apiError(e)); }
  };

  const saveManHours = async () => {
    try {
      const res = await api.put("/settings/safety", { man_hours: Number(manHours) });
      toast.success(res?.data?._offline ? t("savedOffline") : "✓");
      if (!res?.data?._offline) load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const del = async (kind, itemId) => {
    if (!window.confirm(t("confirmDelete"))) return;
    const res = await api.delete(`/safety/${kind}/${itemId}`);
    if (res?.data?._offline) {
      if (kind === "incidents") setIncidents((p) => p.filter((x) => x.id !== itemId));
      else setInspections((p) => p.filter((x) => x.id !== itemId));
      toast.success(t("savedOffline"));
    } else {
      load();
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <p className="overline-label">{t("safety")}</p>
          <h1 className="font-heading font-black text-4xl tracking-tight text-slate-900 mt-1">{t("safety")}</h1>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" data-testid="export-safety-button" className="gap-2"><Export size={16} weight="bold" /> {t("export")}</Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="bg-white" align="end">
            <DropdownMenuItem data-testid="export-safety-pdf-portrait" onClick={async () => { try { await downloadFile("/safety/exports?fmt=pdf&orient=portrait", "reporte_seguridad.pdf"); } catch (e) { toast.error(apiError(e)); } }}>{t("safety")} — PDF ({t("orientPortrait")})</DropdownMenuItem>
            <DropdownMenuItem data-testid="export-safety-pdf-landscape" onClick={async () => { try { await downloadFile("/safety/exports?fmt=pdf&orient=landscape", "reporte_seguridad.pdf"); } catch (e) { toast.error(apiError(e)); } }}>{t("safety")} — PDF ({t("orientLandscape")})</DropdownMenuItem>
            <DropdownMenuItem data-testid="export-safety-xlsx" onClick={async () => { try { await downloadFile("/safety/exports?fmt=xlsx", "reporte_seguridad.xlsx"); } catch (e) { toast.error(apiError(e)); } }}>{t("safety")} — Excel</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {kpis && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <KPI testid="safety-kpi-total" label={t("totalIncidents")} value={kpis.total_incidents} />
          <KPI testid="safety-kpi-open" label={t("openIncidents")} value={kpis.open_incidents} color="text-red-500" />
          <KPI testid="safety-kpi-lti" label="LTI" value={kpis.lost_time_incidents} color="text-amber-600" />
          <KPI testid="safety-kpi-days-lost" label={t("daysLost")} value={kpis.days_lost} />
          <KPI testid="safety-kpi-frequency" label={t("frequencyRate")} value={kpis.frequency_rate} color="text-amber-600" />
          <KPI testid="safety-kpi-compliance" label={t("avgCompliance")} value={`${kpis.avg_compliance}%`} color="text-emerald-600" />
        </div>
      )}

      {canManage && (
        <div className="bg-white border border-slate-200 rounded-lg p-4 flex items-end gap-3 max-w-md">
          <div className="space-y-1.5 flex-1">
            <Label className="overline-label">{t("manHours")}</Label>
            <Input data-testid="man-hours-input" type="number" min={1} value={manHours} onChange={(e) => setManHours(e.target.value)} />
          </div>
          <Button data-testid="man-hours-save-button" onClick={saveManHours} disabled={!manHours || Number(manHours) <= 0} className="bg-blue-900 hover:bg-blue-800">{t("save")}</Button>
        </div>
      )}

      <Tabs defaultValue="incidents">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="incidents" data-testid="tab-incidents">{t("incidents")}</TabsTrigger>
          <TabsTrigger value="inspections" data-testid="tab-inspections">{t("inspections")}</TabsTrigger>
        </TabsList>

        <TabsContent value="incidents" className="space-y-4 mt-4">
          {canWrite && (
            <Dialog open={incOpen} onOpenChange={setIncOpen}>
              <DialogTrigger asChild>
                <Button data-testid="new-incident-button" onClick={() => { setEditingInc(null); setIncForm({ date: "", type: "near_miss", severity: 1, description: "", area: "", status: "open", days_lost: 0 }); }} className="bg-blue-900 hover:bg-blue-800 gap-2"><Warning size={16} weight="bold" /> {t("newIncident")}</Button>
              </DialogTrigger>
              <DialogContent className="bg-white max-w-lg">
                <DialogHeader><DialogTitle className="font-heading">{editingInc ? t("edit") : t("newIncident")}</DialogTitle></DialogHeader>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5"><Label className="overline-label">{t("date")}</Label>
                    <DateField testid="incident-date" value={incForm.date} onChange={(v) => setIncForm({ ...incForm, date: v })} /></div>
                  <div className="space-y-1.5"><Label className="overline-label">{t("type")}</Label>
                    <Select value={incForm.type} onValueChange={(v) => setIncForm({ ...incForm, type: v })}>
                      <SelectTrigger data-testid="incident-type-select"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-white">{INCIDENT_TYPES.map((x) => <SelectItem key={x} value={x}>{t(x)}</SelectItem>)}</SelectContent>
                    </Select></div>
                  <div className="space-y-1.5"><Label className="overline-label">{t("severity")} (1-5)</Label>
                    <Select value={String(incForm.severity)} onValueChange={(v) => setIncForm({ ...incForm, severity: v })}>
                      <SelectTrigger data-testid="incident-severity-select"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-white">{[1, 2, 3, 4, 5].map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}</SelectContent>
                    </Select></div>
                  <div className="space-y-1.5"><Label className="overline-label">{t("area")}</Label>
                    <Input data-testid="incident-area-input" value={incForm.area} onChange={(e) => setIncForm({ ...incForm, area: e.target.value })} /></div>
                  <div className="space-y-1.5"><Label className="overline-label">{t("status")}</Label>
                    <Select value={incForm.status} onValueChange={(v) => setIncForm({ ...incForm, status: v })}>
                      <SelectTrigger data-testid="incident-status-select"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-white">{INCIDENT_STATUSES.map((x) => <SelectItem key={x} value={x}>{t(x)}</SelectItem>)}</SelectContent>
                    </Select></div>
                  <div className="space-y-1.5"><Label className="overline-label">{t("daysLost")}</Label>
                    <Input data-testid="incident-days-lost-input" type="number" value={incForm.days_lost} onChange={(e) => setIncForm({ ...incForm, days_lost: e.target.value })} /></div>
                  <div className="col-span-2 space-y-1.5"><Label className="overline-label">{t("description")}</Label>
                    <Textarea data-testid="incident-description-input" value={incForm.description} onChange={(e) => setIncForm({ ...incForm, description: e.target.value })} /></div>
                </div>
                <Button data-testid="incident-save-button" onClick={addIncident} disabled={!incForm.date} className="bg-blue-900 hover:bg-blue-800 w-full">{editingInc ? t("update") : t("create")}</Button>
              </DialogContent>
            </Dialog>
          )}
          <div className="bg-white border border-slate-200 rounded-lg">
            <table className="w-full data-table">
              <thead><tr><th>{t("date")}</th><th>{t("type")}</th><th>{t("severity")}</th><th>{t("area")}</th><th>{t("description")}</th><th>{t("status")}</th><th>{t("daysLost")}</th>{canWrite && <th></th>}</tr></thead>
              <tbody data-testid="incidents-table-body">
                {incidents.map((i) => (
                  <tr key={i.id}>
                    <td className="font-mono text-xs">{i.date}</td>
                    <td className="text-xs uppercase tracking-wider font-semibold text-slate-600">{t(i.type)}</td>
                    <td><span className={`text-[10px] font-bold px-2 py-1 rounded ${SEV_STYLE[i.severity]}`}>{i.severity}</span></td>
                    <td className="text-slate-500">{i.area}</td>
                    <td className="max-w-xs truncate" title={i.description}>{i.description}</td>
                    <td className="text-xs uppercase tracking-wider font-semibold text-slate-600">{t(i.status)}</td>
                    <td>{i.days_lost}</td>
                    {canWrite && (
                      <td className="whitespace-nowrap">
                        <button data-testid={`incident-edit-${i.id}`} className="text-slate-400 hover:text-blue-900 transition-colors mr-2"
                          onClick={() => {
                            setEditingInc(i.id);
                            setIncForm({ date: i.date, type: i.type, severity: i.severity, description: i.description, area: i.area, status: i.status, days_lost: i.days_lost });
                            setIncOpen(true);
                          }}><PencilSimple size={16} /></button>
                        {canManage && <button data-testid={`incident-delete-${i.id}`} onClick={() => del("incidents", i.id)} className="text-slate-400 hover:text-red-500 transition-colors"><Trash size={16} /></button>}
                      </td>
                    )}
                  </tr>
                ))}
                {!incidents.length && <tr><td colSpan={8} className="text-center text-slate-400 py-10">{t("noData")}</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="inspections" className="space-y-4 mt-4">
          {canWrite && (
            <Dialog open={inspOpen} onOpenChange={setInspOpen}>
              <DialogTrigger asChild>
                <Button data-testid="new-inspection-button" onClick={() => { setEditingInsp(null); setInspForm({ date: "", area: "", inspector: "", findings: "", status: "open", compliance_score: 100 }); }} className="bg-blue-900 hover:bg-blue-800 gap-2"><HardHat size={16} weight="bold" /> {t("newInspection")}</Button>
              </DialogTrigger>
              <DialogContent className="bg-white max-w-lg">
                <DialogHeader><DialogTitle className="font-heading">{editingInsp ? t("edit") : t("newInspection")}</DialogTitle></DialogHeader>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5"><Label className="overline-label">{t("date")}</Label>
                    <DateField testid="inspection-date" value={inspForm.date} onChange={(v) => setInspForm({ ...inspForm, date: v })} /></div>
                  <div className="space-y-1.5"><Label className="overline-label">{t("area")}</Label>
                    <Input data-testid="inspection-area-input" value={inspForm.area} onChange={(e) => setInspForm({ ...inspForm, area: e.target.value })} /></div>
                  <div className="space-y-1.5"><Label className="overline-label">{t("inspector")}</Label>
                    <Input data-testid="inspection-inspector-input" value={inspForm.inspector} onChange={(e) => setInspForm({ ...inspForm, inspector: e.target.value })} /></div>
                  <div className="space-y-1.5"><Label className="overline-label">{t("compliance")} (%)</Label>
                    <Input data-testid="inspection-compliance-input" type="number" min={0} max={100} value={inspForm.compliance_score} onChange={(e) => setInspForm({ ...inspForm, compliance_score: e.target.value })} /></div>
                  <div className="col-span-2 space-y-1.5"><Label className="overline-label">{t("findings")}</Label>
                    <Textarea data-testid="inspection-findings-input" value={inspForm.findings} onChange={(e) => setInspForm({ ...inspForm, findings: e.target.value })} /></div>
                </div>
                <Button data-testid="inspection-save-button" onClick={addInspection} disabled={!inspForm.date || !inspForm.area} className="bg-blue-900 hover:bg-blue-800 w-full">{editingInsp ? t("update") : t("create")}</Button>
              </DialogContent>
            </Dialog>
          )}
          <div className="bg-white border border-slate-200 rounded-lg">
            <table className="w-full data-table">
              <thead><tr><th>{t("date")}</th><th>{t("area")}</th><th>{t("inspector")}</th><th>{t("findings")}</th><th>{t("compliance")}</th>{canWrite && <th></th>}</tr></thead>
              <tbody data-testid="inspections-table-body">
                {inspections.map((i) => (
                  <tr key={i.id}>
                    <td className="font-mono text-xs">{i.date}</td>
                    <td className="text-slate-500">{i.area}</td>
                    <td>{i.inspector}</td>
                    <td className="max-w-md truncate" title={i.findings}>{i.findings}</td>
                    <td>
                      <span className={`font-bold text-sm ${i.compliance_score >= 90 ? "text-emerald-600" : i.compliance_score >= 70 ? "text-amber-600" : "text-red-500"}`}>{i.compliance_score}%</span>
                    </td>
                    {canWrite && (
                      <td className="whitespace-nowrap">
                        <button data-testid={`inspection-edit-${i.id}`} className="text-slate-400 hover:text-blue-900 transition-colors mr-2"
                          onClick={() => {
                            setEditingInsp(i.id);
                            setInspForm({ date: i.date, area: i.area, inspector: i.inspector, findings: i.findings, status: i.status, compliance_score: i.compliance_score });
                            setInspOpen(true);
                          }}><PencilSimple size={16} /></button>
                        {canManage && <button data-testid={`inspection-delete-${i.id}`} onClick={() => del("inspections", i.id)} className="text-slate-400 hover:text-red-500 transition-colors"><Trash size={16} /></button>}
                      </td>
                    )}
                  </tr>
                ))}
                {!inspections.length && <tr><td colSpan={6} className="text-center text-slate-400 py-10">{t("noData")}</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
